import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import TypeCheckbox from '../form/form-fields/TypeCheckbox';

// Every inline attachment and the positions that make it available. Used by
// the step itself, by the stepper to hide the step, and to drop a selected
// attachment once its source positions are removed.
const INLINE_ATTACHMENT_TYPES = [
  {
    key: 'hoaiCalculation',
    isAvailable: (positions) => positions.some((p) => p.type === 'hoaiPhase'),
  },
  {
    key: 'timesheet',
    isAvailable: (positions) =>
      positions.some((p) => p.timeTrackingIds && p.timeTrackingIds.length > 0),
  },
];

export function availableInlineAttachments(positions) {
  const list = Array.isArray(positions) ? positions : [];
  return INLINE_ATTACHMENT_TYPES.filter((type) => type.isAvailable(list)).map(
    (type) => type.key,
  );
}

export function hasInlineAttachments(positions) {
  return availableInlineAttachments(positions).length > 0;
}

/**
 * Removes selected attachments whose source positions no longer exist, e.g.
 * the HOAI calculation after the HOAI positions were deleted. Returns the
 * partial dataCollection update, or null when nothing has to change, so the
 * stepper can call it on every change without looping.
 */
export function pruneInlineAttachments(dataCollection) {
  const selected = dataCollection?.inlineAttachments;
  if (!Array.isArray(selected) || selected.length === 0) return null;

  // No positions key at all means they are not loaded yet (e.g. a draft whose
  // positions arrive in a later update), not that there are none. Leave the
  // stored selection alone until the positions are known.
  if (!Array.isArray(dataCollection?.positions)) return null;

  const available = availableInlineAttachments(dataCollection.positions);
  const kept = selected.filter((key) => available.includes(key));
  if (kept.length === selected.length) return null;

  return { inlineAttachments: kept };
}

export const StepInlineAttachments = ({
  dataCollection,
  updateDataCollection,
  updateValidation,
}) => {
  const { t } = useTranslation();

  const labels = {
    hoaiCalculation: t('HOAI Calculation'),
    timesheet: t('Timesheet'),
  };

  const available = availableInlineAttachments(dataCollection?.positions);

  useEffect(() => {
    updateValidation(true);
  }, []);

  function handleAttachmentChange(key) {
    const current = dataCollection?.inlineAttachments || [];
    const newInlineAttachments = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    updateDataCollection({ inlineAttachments: newInlineAttachments });
  }

  return (
    <Box className="flex flex-col gap-2">
      <Typography fontWeight={500} fontSize={16}>
        {t('Which attachments should be automatically generated and sent?')}
      </Typography>
      {available.map((key) => (
        <TypeCheckbox
          key={key}
          field={{ key, label: labels[key] }}
          dataCollection={{
            [key]: dataCollection?.inlineAttachments?.includes(key),
          }}
          updateValidationOnChange={() => handleAttachmentChange(key)}
        />
      ))}
    </Box>
  );
};
